import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Alert, Chip, Empty, Money, Note, PageHeader, Panel, Spinner, Table, Tabs, Tiles } from '../../ui';
import {
  dueRuleSourceWords, dueRuleWords, firstTab, isCredit, magnitude,
  type CurrencyAmount, type InvoiceRow, type MyPayables, type StatementLine,
} from '../payables-view';
import { invoiceAsked } from './delivery-tally';

/**
 * R-22 Payables — FN-07, FN-08, FN-09, DB-03. The retailer's side of the figure A-40 shows Accounts.
 *
 * What is owed, per currency and never combined (AN-08); what is overdue; when the next payment
 * falls due; the invoices (where the market invoices per sale) or the payment history (where it
 * settles monthly); and the indicative statement with its running balance. Every figure is the
 * API's (GET /shop/payables), composed from the same functions A-40 and A-42 use, so the retailer
 * and Miraki read one number. A payable below zero is a CREDIT — money Miraki owes the retailer
 * (FN-08) — and is said so, never shown as nothing.
 *
 * Floor: `shop.ledger`, which DB-07 gives the Owner and Accounts and not the Buyer. A Buyer is not
 * sent these figures by the server; this screen does not hide anything that arrived.
 *
 * FN-07, uploading a proof of payment, is optional and never required. There is no table for a
 * proof against a PURCHASE invoice yet (the v1.5 proof belongs to an order), so the screen says how to
 * pay and that the upload is coming, rather than offer a control that cannot keep the file.
 *
 * Replaces R-16 Statement (Screen Flow v0.3).
 */

type Tab = 'invoices' | 'payments' | 'statement';

function Owed({ list, none, t }: { list: readonly CurrencyAmount[]; none: string; t: (k: string) => string }) {
  if (list.length === 0) return <span className="mk-dim">{none}</span>;
  return (
    <>
      {list.map((a) => (
        <div key={a.currency}>
          {isCredit(a.amount)
            ? <>{t('payables.creditOf')} <Money amount={magnitude(a.amount)} currency={a.currency} /></>
            : <Money amount={a.amount} currency={a.currency} />}
        </div>
      ))}
    </>
  );
}

export function ShopPayables() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['shop', 'payables'], queryFn: () => api.get<MyPayables>('/shop/payables'),
  });
  const [chosen, setChosen] = useState<Tab | null>(null);
  // W5 — R-07's tally links here naming one invoice (`?invoice=<reference>`). Honouring it is what
  // makes "View invoice" land ON the invoice rather than near it: the invoices tab opens whatever
  // the due rule would otherwise have chosen, and the row is marked. An unknown reference marks
  // nothing and is not an error — the invoice may have been cancelled since the link was made.
  const asked = invoiceAsked(useLocation().search);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  const d = data!;
  const tab: Tab = chosen ?? (asked ? 'invoices' : firstTab(d.dueRule));
  const rule = dueRuleWords(d.dueRule);
  const source = dueRuleSourceWords(d.dueRule, 'retailer');
  const tr = (k: string) => t(k);

  return (
    <>
      <PageHeader title={t('payables.title')} sub={t('payables.asOf', { portal: d.portal.name, asOf: d.asOf })} />

      <Tiles items={[
        { k: t('payables.outstanding'), v: <Owed list={d.outstanding} none={t('payables.nothingOwed')} t={tr} />, s: t('payables.outstandingHint') },
        { k: t('payables.overdue'), v: d.overdue.length ? <Owed list={d.overdue} none="" t={tr} /> : <span className="mk-dim">{t('payables.nothingOverdue')}</span> },
        { k: t('payables.nextDue'), v: d.nextDueOn ?? <span className="mk-dim">{t('payables.noneDue')}</span> },
      ]} />

      {/*
        * D75 — the rule in force, then WHERE it came from.
        *
        * `payables.dueFixed` used to sit here unconditionally: "This is set by Miraki for your
        * portal." That is now false for an account with its own payment term, and falsely
        * disclaims a term the retailer actually agreed to. `dueRuleSourceWords` reads the
        * resolved `source` instead, so the second sentence follows the first.
        */}
      <Note>
        {t(rule.key, rule.values)} {t(source.key)} {t('payables.howToPay')}
      </Note>

      <Tabs
        active={tab}
        onChange={(id) => setChosen(id as Tab)}
        tabs={[
          { id: 'invoices', label: t('payables.tabInvoices'), ...(d.invoices.some((i) => i.overdue) ? { badge: '!' } : {}) },
          { id: 'payments', label: t('payables.tabPayments') },
          { id: 'statement', label: t('payables.tabStatement') },
        ]}
      />

      {tab === 'invoices' ? (
        <Panel pad>
          {d.invoices.length === 0 ? (
            <Empty title={t('payables.invoicesEmpty')} body={t('payables.invoicesEmptyBody')} />
          ) : (
            <Table<InvoiceRow>
              rows={d.invoices}
              rowKey={(i) => i.id}
              rowClassName={(i) => (asked !== null && i.reference === asked ? 'on' : undefined)}
              columns={[
                { header: t('payables.colReference'), render: (i) => <Link to={`/shop/payables/invoices/${i.id}`}><code>{i.reference}</code></Link> },
                { header: t('payables.colIssued'), render: (i) => i.issuedOn },
                { header: t('payables.colDue'), render: (i) => i.dueOn },
                { header: t('payables.colAmount'), numeric: true, render: (i) => <Money amount={i.amount} currency={i.currency} /> },
                { header: t('payables.colOpen'), numeric: true, render: (i) => <Money amount={i.open} currency={i.currency} /> },
                {
                  header: t('payables.colState'),
                  render: (i) => (i.overdue
                    ? <Chip tone="stop">{t('payables.overdueChip')}</Chip>
                    : <Chip tone={i.state === 'PAID' ? 'done' : i.state === 'CANCELLED' ? 'lapse' : 'us'}>{t(`payables.state.${i.state}`)}</Chip>),
                },
              ]}
            />
          )}
          <p className="mk-hint">{t('payables.proofComing')}</p>
        </Panel>
      ) : null}

      {tab === 'payments' ? (
        <Panel pad>
          {d.payments.length === 0 ? (
            <Empty title={t('payables.paymentsEmpty')} body={t('payables.paymentsEmptyBody')} />
          ) : (
            <Table
              rows={d.payments}
              rowKey={(p) => p.id}
              columns={[
                { header: t('payables.colReceived'), render: (p) => p.receivedOn },
                { header: t('payables.colBankReference'), render: (p) => p.bankReference ?? '—' },
                { header: t('payables.colAmount'), numeric: true, render: (p) => <Money amount={p.amount} currency={p.currency} /> },
                {
                  header: t('payables.colAppliedTo'),
                  render: (p) => (
                    <>
                      {p.allocations.map((a) => (
                        <div key={a.reference}><code>{a.reference}</code> <Money amount={a.amount} currency={p.currency} /></div>
                      ))}
                      {!isCredit(p.unallocated) && p.unallocated !== '0.0000'
                        ? <div className="mk-hint">{t('payables.leftAsCredit')} <Money amount={p.unallocated} currency={p.currency} /></div>
                        : null}
                    </>
                  ),
                },
              ]}
            />
          )}
        </Panel>
      ) : null}

      {tab === 'statement' ? (
        <>
          <Note>{t('payables.statementNote')}</Note>
          {d.statement.length === 0 ? (
            <Panel pad><Empty title={t('payables.statementEmpty')} body={t('payables.statementEmptyBody')} /></Panel>
          ) : d.statement.map((s) => (
            <Panel key={s.currency} title={t('payables.statementIn', { currency: s.currency })} pad>
              <Table<StatementLine>
                rows={s.lines}
                rowKey={(l) => l.id}
                columns={[
                  { header: t('payables.colDate'), render: (l) => l.occurredAt.slice(0, 10) },
                  { header: t('payables.colEntry'), render: (l) => <>{t(`payables.entry.${l.entryType}`)}{l.reason ? <div className="mk-hint">{l.reason}</div> : null}</> },
                  { header: t('payables.colReference'), render: (l) => <code>{l.reference}</code> },
                  { header: t('payables.colDebit'), numeric: true, render: (l) => (l.debit ? <Money amount={l.debit} currency={s.currency} /> : '') },
                  { header: t('payables.colCredit'), numeric: true, render: (l) => (l.credit ? <Money amount={l.credit} currency={s.currency} /> : '') },
                  {
                    header: t('payables.colBalance'), numeric: true,
                    render: (l) => (isCredit(l.balance)
                      ? <span>{t('payables.creditOf')} <Money amount={magnitude(l.balance)} currency={s.currency} /></span>
                      : <Money amount={l.balance} currency={s.currency} />),
                  },
                ]}
              />
              <p style={{ textAlign: 'end' }}>
                {t('payables.closing')}{' '}
                <b>{isCredit(s.closing)
                  ? <>{t('payables.creditOf')} <Money amount={magnitude(s.closing)} currency={s.currency} /></>
                  : <Money amount={s.closing} currency={s.currency} />}</b>
              </p>
            </Panel>
          ))}
        </>
      ) : null}
    </>
  );
}
